import { useStore } from "react-redux";
import {
  USER_ADDRESS_ACTION_ENUM,
  UserAddressPayload,
} from "../reducers/userAddress";
import { useSelector } from "./useSelector";
import { Action } from "../../types/redux";
import { AddressFormType, AddressTypeEnum } from "../../types";
import UserAddressService from "../../service/UserAddressService";
import { userAddressFormTypeToUserAddress } from "../../utils/userAddress.utils";

export const useUserAddress = () => {
  const { dispatch } = useStore();
  const {
    isUserAddressLoading,
    isUserAddressLoaded,
    shippingAddresses,
    billingAddresses,
  } = useSelector((state) => state.userAddress);

  const getAllUserAddresses = async () => {
    const addresses = (await UserAddressService.getUserAddresses())?.data;
    const shippingAddresses = addresses?.filter(
      (address) => address.type === AddressTypeEnum.SHIPPING
    );
    const billingAddresses = addresses?.filter(
      (address) => address.type === AddressTypeEnum.BILLING
    );
    dispatch<Action<USER_ADDRESS_ACTION_ENUM, UserAddressPayload>>({
      type: USER_ADDRESS_ACTION_ENUM.SET_USER_ADDRESSES,
      payload: {
        shippingAddresses: shippingAddresses ?? [],
        billingAddresses: billingAddresses ?? [],
      },
      error: null,
    });
  };

  const createUserAddress = async (address: AddressFormType) => {
    const res = await UserAddressService.createUserAddress(address);
    await getAllUserAddresses();
    return res.data;
  };

  const addOneTimeUsedAddress = (
    address: AddressFormType,
    addressType: AddressTypeEnum
  ) => {
    dispatch<Action<USER_ADDRESS_ACTION_ENUM, UserAddressPayload>>({
      type: USER_ADDRESS_ACTION_ENUM.SET_USER_ADDRESSES,
      payload: {
        shippingAddresses:
          addressType === AddressTypeEnum.SHIPPING
            ? [
                ...shippingAddresses.filter(
                  (address) => address.id !== "one_time_used"
                ),
                userAddressFormTypeToUserAddress(address),
              ]
            : shippingAddresses,
        billingAddresses:
          addressType === AddressTypeEnum.BILLING
            ? [
                ...billingAddresses.filter(
                  (address) => address.id !== "one_time_used"
                ),
                userAddressFormTypeToUserAddress(address),
              ]
            : billingAddresses,
      },
      error: null,
    });
  };

  const deleteOneTimeUsedAddress = (addressType: AddressTypeEnum) => {
    dispatch<Action<USER_ADDRESS_ACTION_ENUM, UserAddressPayload>>({
      type: USER_ADDRESS_ACTION_ENUM.SET_USER_ADDRESSES,
      payload: {
        shippingAddresses:
          addressType === AddressTypeEnum.SHIPPING
            ? [
                ...shippingAddresses.filter(
                  (address) => address.id !== "one_time_used"
                ),
              ]
            : shippingAddresses,
        billingAddresses:
          addressType === AddressTypeEnum.BILLING
            ? [
                ...billingAddresses.filter(
                  (address) => address.id !== "one_time_used"
                ),
              ]
            : billingAddresses,
      },
      error: null,
    });
  };

  const deleteOneTimeUsedAddresses = () => {
    dispatch<Action<USER_ADDRESS_ACTION_ENUM, UserAddressPayload>>({
      type: USER_ADDRESS_ACTION_ENUM.REMOVE_ONE_TIME_USED_ADDRESS,
      payload: {
        shippingAddresses: shippingAddresses.filter(
          (address) => address.id !== "one_time_used"
        ),
        billingAddresses: billingAddresses.filter(
          (address) => address.id !== "one_time_used"
        ),
      },
      error: null,
    });
  };

  return {
    getAllUserAddresses,
    isUserAddressLoading,
    isUserAddressLoaded,
    shippingAddresses,
    billingAddresses,
    createUserAddress,
    addOneTimeUsedAddress,
    deleteOneTimeUsedAddress,
    deleteOneTimeUsedAddresses,
  };
};
